import { useEffect } from "react";
import { Link } from "react-router-dom";
import ZaidPhoto from "../assests/Zaid_photo.webp";
import VikasPhoto from "../assests/Vikas_photo.webp";
import Reveal from "../components/Reveal";

const TIMELINE = [
  {
    year: "2018",
    title: "A studio is born",
    body: "TABA — The Art By Artist — is founded in Allahabad by Vikas Mandal and Mohammed Zaid. Two brushes, one wall.",
  },
  {
    year: "2019",
    title: "First public walls",
    body: "Pilgrimage art on the banks of the Ganga. Murals at Naini Railway Station during MahaKumbh.",
  },
  {
    year: "2021",
    title: "Moving to Delhi",
    body: "The studio relocates to South Delhi. School walls, café walls, foundation projects — scale grows.",
  },
  {
    year: "2023",
    title: "Heritage & civic walls",
    body: "Flyover pillars under Sambharya Foundation. CSR work with Renukiran across Delhi NCR schools.",
  },
  {
    year: "Today",
    title: "Still painting by hand",
    body: "120+ walls, six cities, one studio. A small team with a slow, careful hand.",
  },
];

const VALUES = [
  {
    n: "01",
    t: "Hand over digital",
    b: "Every mural is brushed by a real hand, on a real wall. We don't print. We don't vinyl. We paint.",
  },
  {
    n: "02",
    t: "Listen over decorate",
    b: "We never begin with a stock design. Every wall begins with a long conversation with the place.",
  },
  {
    n: "03",
    t: "Slow over fast",
    b: "We take on a small number of commissions every year. Each one is sketched, scaled, and brushed slowly.",
  },
  {
    n: "04",
    t: "Place over portfolio",
    b: "A mural is meant to age in the space it lives. We document it — but the wall belongs to its place.",
  },
];

function About() {
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  return (
    <main className="bg-[var(--bg)] text-[var(--bone)] pt-32 pb-24">
      {/* HERO */}
      <section className="px-5 md:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <Reveal>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                — About TABA
              </div>
              <div className="mt-3 font-display text-5xl md:text-7xl text-[var(--bone)]">
                01
              </div>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-9">
            <Reveal delay={120}>
              <h1 className="font-display font-light text-[clamp(2.5rem,6.5vw,6rem)] leading-[1] tracking-tight">
                Two hands, <span className="italic font-extralight">one</span>{" "}
                studio,{" "}
                <span className="text-[var(--clay)]">120+</span> walls.
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-10 max-w-2xl text-[var(--muted)] text-[17px] leading-relaxed">
                TABA is a Delhi-based mural art studio. Founded in 2018 by
                Vikas Mandal and Mohammed Zaid, we paint bold, large-scale
                stories on walls across India — from cafés and schools to
                flyovers and heritage sites.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MANIFESTO BIG QUOTE */}
      <section className="px-5 md:px-10 mt-24 md:mt-40">
        <div className="max-w-7xl mx-auto editorial-frame bg-[var(--bg-soft)] p-10 md:p-20">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)] mb-8">
              — On the practice
            </div>
            <p className="font-display font-light text-[clamp(1.8rem,4vw,3.5rem)] leading-[1.15] tracking-tight">
              <span className="text-[var(--clay)]">"</span>A wall is never
              just a wall. It is a public memory, a shared sentence, a quiet
              protest. We don't decorate walls. We give them a{" "}
              <span className="italic font-extralight">voice</span> — and then
              we let them speak for years to come.
              <span className="text-[var(--clay)]">"</span>
            </p>
            <div className="mt-10 flex items-baseline gap-3">
              <span className="block w-10 h-px bg-[var(--clay)]" />
              <span className="text-[10px] uppercase tracking-[0.32em] text-[var(--muted)]">
                TABA Studio Practice · Since 2018
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="px-5 md:px-10 mt-24 md:mt-40">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-6 mb-16">
            <div className="col-span-12 md:col-span-3">
              <Reveal>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                  — What we believe
                </div>
                <div className="mt-3 font-display text-5xl md:text-7xl text-[var(--bone)]">
                  02
                </div>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-9">
              <Reveal delay={120}>
                <h2 className="font-display font-light text-[clamp(2.5rem,5.5vw,5rem)] leading-[1.05] tracking-tight">
                  Four things we{" "}
                  <span className="italic font-extralight">refuse</span> to
                  compromise on.
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-[var(--line)] border-t border-b border-[var(--line)]">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={i * 80}>
                <div className="group relative bg-[var(--bg)] p-8 md:p-12 h-full hover:bg-[var(--bg-soft)] transition-colors">
                  <div className="flex items-start justify-between mb-6">
                    <div className="font-display text-3xl md:text-4xl text-[var(--clay-light)]">
                      {v.n}
                    </div>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl text-[var(--bone)] mb-4">
                    {v.t}
                  </h3>
                  <p className="text-[var(--muted)] text-[15px] leading-relaxed max-w-md">
                    {v.b}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section className="px-5 md:px-10 mt-24 md:mt-40">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-6 mb-16">
            <div className="col-span-12 md:col-span-3">
              <Reveal>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                  — The hands
                </div>
                <div className="mt-3 font-display text-5xl md:text-7xl text-[var(--bone)]">
                  03
                </div>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-9">
              <Reveal delay={120}>
                <h2 className="font-display font-light text-[clamp(2.5rem,5.5vw,5rem)] leading-[1.05] tracking-tight">
                  Three artists. One studio.{" "}
                  <span className="italic font-extralight">A wall</span> waiting
                  for its story.
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-10">
            <Reveal id="mohammadzaid">
              <div className="group block relative overflow-hidden bg-[var(--bg-elevated)] editorial-frame">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={ZaidPhoto}
                    alt="Mohammed Zaid"
                    loading="lazy"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-transparent to-transparent" />

                <div className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.32em] text-[var(--bone)]/80 flex items-center gap-2">
                  <span className="font-display text-2xl text-[var(--clay)] leading-none tabular-nums">
                    01
                  </span>
                  <span className="block w-6 h-px bg-[var(--bone)]/60" />
                  Co-founder
                </div>

                <div className="absolute left-0 right-0 bottom-0 p-6 md:p-10">
                  <h3 className="font-display text-3xl md:text-5xl text-[var(--bone)] leading-none">
                    Mohammed Zaid
                  </h3>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-[var(--clay-light)]">
                    Co-founder · Lead Muralist
                  </div>
                  <p className="mt-4 text-[var(--bone)]/80 max-w-md text-[15px] leading-relaxed">
                    MFA in painting. Zaid's hand draws the gesture, the figure,
                    the loud colour. He leads the studio's on-site practice.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal id="vikasmandal" delay={120}>
              <div className="group block relative overflow-hidden bg-[var(--bg-elevated)] editorial-frame">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={VikasPhoto}
                    alt="Vikas Mandal"
                    loading="lazy"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-transparent to-transparent" />

                <div className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.32em] text-[var(--bone)]/80 flex items-center gap-2">
                  <span className="font-display text-2xl text-[var(--clay)] leading-none tabular-nums">
                    02
                  </span>
                  <span className="block w-6 h-px bg-[var(--bone)]/60" />
                  Co-founder
                </div>

                <div className="absolute left-0 right-0 bottom-0 p-6 md:p-10">
                  <h3 className="font-display text-3xl md:text-5xl text-[var(--bone)] leading-none">
                    Vikas Mandal
                  </h3>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-[var(--clay-light)]">
                    Co-founder · Studio Director
                  </div>
                  <p className="mt-4 text-[var(--bone)]/80 max-w-md text-[15px] leading-relaxed">
                    BA, MA (open). Vikas shapes the studio's visual language —
                    composition, palette, and the quiet decisions behind every
                    wall.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="px-5 md:px-10 mt-24 md:mt-40">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-6 mb-16">
            <div className="col-span-12 md:col-span-3">
              <Reveal>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                  — The journey
                </div>
                <div className="mt-3 font-display text-5xl md:text-7xl text-[var(--bone)]">
                  04
                </div>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-9">
              <Reveal delay={120}>
                <h2 className="font-display font-light text-[clamp(2.5rem,5.5vw,5rem)] leading-[1.05] tracking-tight">
                  Seven years,{" "}
                  <span className="italic font-extralight">one brushstroke</span>{" "}
                  at a time.
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="relative border-t border-[var(--line)]">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 80}>
                <div className="grid grid-cols-12 gap-6 py-10 md:py-14 border-b border-[var(--line)] group hover:bg-[var(--bg-soft)] transition-colors">
                  <div className="col-span-12 md:col-span-3">
                    <div className="font-display text-4xl md:text-6xl text-[var(--clay-light)] tabular-nums leading-none">
                      {t.year}
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-9">
                    <h3 className="font-display text-2xl md:text-4xl text-[var(--bone)] leading-tight">
                      {t.title}
                    </h3>
                    <p className="mt-4 text-[var(--muted)] text-[15px] leading-relaxed max-w-xl">
                      {t.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 md:px-10 mt-24 md:mt-40">
        <div className="max-w-7xl mx-auto border-t border-[var(--line)] pt-12 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
              — Come paint with us
            </div>
            <h3 className="mt-3 font-display text-3xl md:text-5xl text-[var(--bone)] leading-tight max-w-xl">
              Have a wall in mind?
            </h3>
          </div>
          <Link
            to="/booknow"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-[var(--clay)] text-[var(--bone)] rounded-full tracking-wide hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors"
          >
            Commission a wall
            <span className="relative w-4 h-4 overflow-hidden">
              <span className="absolute inset-0 transition-transform duration-500 group-hover:translate-x-5 group-hover:-translate-y-5">
                →
              </span>
              <span className="absolute inset-0 -translate-x-5 translate-y-5 transition-transform duration-500 group-hover:translate-x-0 group-hover:translate-y-0">
                →
              </span>
            </span>
          </Link>
        </div>
      </section>
    </main>
  );
}

export default About;